<template>
    <Head title="User Preferences" />
    <BaseLayout gap="lg" fill-height>
        <BasePageHeader title="User Preferences" />
        <AccountSettingsSection
            :user-profile="user_profile"
            :language-choices="language_choices"
            :cookie-consent-choices="cookie_consent_choices"
            :errors="errors[SETTINGS_ERROR_BAG]"
            :endpoints="endpoints"
            :can-receive-emails="userDetails?.can_receive_emails"
            :custom-instructions-enabled="custom_instructions_enabled"
            :mic-consent="mic_consent" />
        <IntegrationsSection
            :integrations-enabled="integrations_enabled"
            :calendar-integration="calendar_integration"
            :granola-enabled="granola_enabled"
            :granola-connected="granola_connected"
            :granola-connect-url="granola_connect_url"
            :granola-disconnect-url="granola_disconnect_url"
            :workiq-integration="workiq_integration"
            :slack-install-url="slack_install_url"
            :slack-connected="slack_connected"
            :mobile-app-download-url="endpoints.mobile_app_download" />
        <DeleteAccountSection v-if="!userDetails?.is_demo" class="mt-auto" :delete-account-url="endpoints.delete_account" />
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import type { UserDetails } from "~vue/appTypes";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import AccountSettingsSection, { ERROR_BAG as SETTINGS_ERROR_BAG } from "~vue/UserProfile/AccountSettingsSection.vue";
import DeleteAccountSection from "~vue/UserProfile/DeleteAccountSection.vue";
import IntegrationsSection from "~vue/UserProfile/IntegrationsSection.vue";
import type { Ref } from "vue";
import { inject, provide } from "vue";

const props = withDefaults(
    defineProps<{
        user_profile: Record<string, unknown>;
        language_choices?: [string, string][];
        cookie_consent_choices?: [string, string][];
        errors?: Record<string, Record<string, string>>;
        endpoints: Record<string, string>;
        calendar_integration?: Partial<{
            enabled: boolean;
            timezones: string[];
            calendar_provider: string | null;
            dismissed: boolean;
            private_toggle_enabled: boolean;
            allow_private_events: boolean | null;
        }>;
        custom_instructions_enabled?: boolean;
        mic_consent?: Partial<{ policy_enabled: boolean; granted: boolean }>;
        granola_enabled?: boolean;
        granola_connected?: boolean;
        granola_connect_url?: string;
        granola_disconnect_url?: string;
        workiq_integration?: Partial<{
            enabled: boolean;
            connected: boolean;
            connect_url: string;
            status_url: string;
            disconnect_url: string;
        }>;
        integrations_enabled?: boolean;
        slack_install_url?: string;
        slack_connected?: boolean;
    }>(),
    {
        language_choices: () => [],
        cookie_consent_choices: () => [],
        errors: () => ({}) as Record<string, Record<string, string>>,
        calendar_integration: () => ({}),
        custom_instructions_enabled: false,
        mic_consent: () => ({}),
        granola_enabled: false,
        granola_connected: false,
        granola_connect_url: "",
        granola_disconnect_url: "",
        workiq_integration: () => ({}),
        integrations_enabled: false,
        slack_install_url: "",
        slack_connected: false,
    },
);

provide("calendarIntegration", props.calendar_integration);

const userDetails = inject<Ref<UserDetails>>("user_details");
</script>
